import { forwardRef, type PropsWithChildren } from "react";

import LoadingCircularIcon from "$/lib/icons/LoadingCircularIcon";
import { cn } from "$/lib/utils/functions/misc.functions";

import type { ButtonProps } from "./Button.types";

const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(
  (
    {
      className,
      isLoading,
      disabled,
      type,
      theme = "primary",
      size = "medium",
      outlined,
      children,
      ...buttonProps
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        type={type || "button"}
        aria-busy={isLoading}
        className={cn(
          "flex h-fit items-center justify-center gap-2 rounded-lg font-medium transition-all duration-200 hover:opacity-85 hover:shadow-sm active:opacity-80 disabled:cursor-not-allowed disabled:bg-textInfo disabled:opacity-80 aria-busy:cursor-wait",
          {
            "bg-primary text-white": theme === "primary",
            "bg-secondary text-white": theme === "secondary",
            "bg-danger text-white": theme === "danger",
            "bg-[#00D9A5] text-white": theme === "success",
            "bg-transparent text-textColor disabled:bg-transparent disabled:opacity-50":
              theme === "ghost",
          },
          {
            "px-3 py-1.5": size === "small",
            "px-4 py-2": size === "medium",
            "px-6 py-3": size === "large",
          },
          outlined && "bg-snow border text-primary",
          className,
        )}
        disabled={disabled || isLoading}
        {...buttonProps}
      >
        {children}{" "}
        {isLoading && <LoadingCircularIcon className="size-4 animate-spin" />}
      </button>
    );
  },
);

Button.displayName = "Button";

export default Button;
